import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ShiftaskEvaluationCriterion {
  id: string;
  name: string;
  category: string;
  description?: string;
  weight: number;
  is_active: boolean;
}

export interface ShiftaskEvaluationResponse {
  id: string;
  evaluation_id: string;
  criterion_id: string;
  score: number;
  comments?: string;
  criterion?: ShiftaskEvaluationCriterion;
}

export interface ShiftaskEvaluation {
  id: string;
  guard_id: string;
  evaluator_id: string;
  evaluation_date: string;
  overall_score: number;
  status: 'active' | 'archived' | 'pending';
  notes?: string;
  valid_from: string;
  valid_until?: string;
  evaluation_year?: number;
  evaluation_period?: string;
  responses?: ShiftaskEvaluationResponse[];
}

// Hook για φόρτωση κριτηρίων αξιολόγησης
export const useShiftaskCriteria = () => {
  return useQuery({
    queryKey: ['shiftask-criteria'],
    queryFn: async (): Promise<ShiftaskEvaluationCriterion[]> => {
      const { data, error } = await supabase
        .from('ovit_evaluation_criteria')
        .select('*')
        .eq('is_active', true)
        .order('category, name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook για φόρτωση αξιολόγησης φύλακα (τρέχον εξάμηνο)
export const useShiftaskEvaluation = (guardId: string) => {
  return useQuery({
    queryKey: ['shiftask-evaluation', guardId],
    queryFn: async (): Promise<ShiftaskEvaluation | null> => {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';

      const { data, error } = await supabase
        .from('ovit_evaluations')
        .select(`
          *,
          responses:ovit_evaluation_responses(
            *,
            criterion:ovit_evaluation_criteria(*)
          )
        `)
        .eq('guard_id', guardId)
        .eq('status', 'active')
        .eq('evaluation_year', currentYear)
        .eq('evaluation_period', currentSemester)
        .order('evaluation_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as ShiftaskEvaluation | null;
    },
    enabled: !!guardId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook για φόρτωση τρέχουσας αξιολόγησης για επεξεργασία
export const useCurrentShiftaskEvaluation = (guardId: string, evaluatorId: string) => {
  return useQuery({
    queryKey: ['current-shiftask-evaluation', guardId, evaluatorId],
    queryFn: async (): Promise<ShiftaskEvaluation | null> => {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';

      const { data, error } = await supabase
        .from('ovit_evaluations')
        .select('*')
        .eq('guard_id', guardId)
        .eq('evaluator_id', evaluatorId)
        .eq('evaluation_year', currentYear)
        .eq('evaluation_period', currentSemester)
        .eq('status', 'active')
        .order('evaluation_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as ShiftaskEvaluation | null;
    },
    enabled: !!guardId && !!evaluatorId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook για δημιουργία νέας αξιολόγησης
export const useCreateShiftaskEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (evaluation: Omit<ShiftaskEvaluation, 'id'>) => {
      const { data, error } = await supabase
        .from('ovit_evaluations')
        .insert([evaluation] as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Η αξιολόγηση Shiftask δημιουργήθηκε επιτυχώς');
      queryClient.invalidateQueries({ queryKey: ['shiftask-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['current-shiftask-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['shiftask-evaluations-all'] });
    },
    onError: (error: any) => {
      toast.error(`Σφάλμα δημιουργίας αξιολόγησης: ${error.message}`);
    },
  });
};

// Hook για ενημέρωση υπάρχουσας αξιολόγησης
export const useUpdateShiftaskEvaluation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ShiftaskEvaluation> }) => {
      const { data, error } = await supabase
        .from('ovit_evaluations')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Η αξιολόγηση Shiftask ενημερώθηκε επιτυχώς');
      queryClient.invalidateQueries({ queryKey: ['shiftask-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['current-shiftask-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['shiftask-evaluations-all'] });
    },
    onError: (error: any) => {
      toast.error(`Σφάλμα ενημέρωσης αξιολόγησης: ${error.message}`);
    },
  });
};

// Hook για αποθήκευση απαντήσεων αξιολόγησης
export const useCreateShiftaskResponses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (responses: Omit<ShiftaskEvaluationResponse, 'id'>[]) => {
      const { data, error } = await supabase
        .from('ovit_evaluation_responses')
        .insert(responses)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Οι απαντήσεις αξιολόγησης αποθηκεύτηκαν');
      queryClient.invalidateQueries({ queryKey: ['shiftask-evaluation'] });
    },
    onError: (error: any) => {
      toast.error(`Σφάλμα αποθήκευσης απαντήσεων: ${error.message}`);
    },
  });
};